import type { VRMCore } from '@pixiv/three-vrm-core'
import type { Ref } from 'vue'
import type { Profile } from 'wlipsync'

import { useAsyncState } from '@vueuse/core'
import { onUnmounted, watch } from 'vue'
import { createWLipSyncNode } from 'wlipsync'

import profile from '../../assets/lip-sync-profile.json' with { type: 'json' }

import { useAudioContext } from '../../../../stage-ui/src/stores/audio'

export function useVRMLipSync(audioNode: Ref<AudioBufferSourceNode | undefined, AudioBufferSourceNode | undefined>) {
  const { audioContext } = useAudioContext()
  const { state: lipSyncNode, isReady } = useAsyncState(createWLipSyncNode(audioContext, profile as Profile), undefined)

  // https://github.com/mrxz/wLipSync/blob/c3bc4b321dc7e1ca333d75f7aa1e9e746cbbb23a/example/index.js#L50-L66
  const RAW_KEYS = ['A', 'E', 'I', 'O', 'U', 'S'] as const
  type LipKey = 'A' | 'E' | 'I' | 'O' | 'U'
  const LIP_KEYS: LipKey[] = ['A', 'E', 'I', 'O', 'U']

  // NOTICE: Default VRM 1.0 viseme expression names
  // Some models may use different naming conventions (uppercase, custom names, etc.)
  const DEFAULT_BLENDSHAPE_MAP: Record<LipKey, string> = {
    A: 'aa',
    E: 'ee',
    I: 'ih',
    O: 'oh',
    U: 'ou',
  }

  // Alternative names that some VRM models might use
  const ALTERNATIVE_NAMES: Record<LipKey, string[]> = {
    A: ['aa', 'Aa', 'A', 'a', 'Fcl_MTH_A', 'vrc.v_aa'],
    E: ['ee', 'Ee', 'E', 'e', 'Fcl_MTH_E', 'vrc.v_e'],
    I: ['ih', 'Ih', 'I', 'i', 'Fcl_MTH_I', 'vrc.v_ih'],
    O: ['oh', 'Oh', 'O', 'o', 'Fcl_MTH_O', 'vrc.v_oh'],
    U: ['ou', 'Ou', 'U', 'u', 'Fcl_MTH_U', 'vrc.v_ou'],
  }

  // Resolved expression names for the current VRM model
  let resolvedBlendshapeMap: Record<LipKey, string | null> = {
    A: null,
    E: null,
    I: null,
    O: null,
    U: null,
  }
  let lastVrmId: string | null = null
  const RAW_TO_LIP: Record<typeof RAW_KEYS[number], LipKey> = {
    A: 'A',
    E: 'E',
    I: 'I',
    O: 'O',
    U: 'U',
    S: 'I',
  }

  const smoothState: Record<LipKey, number> = { A: 0, E: 0, I: 0, O: 0, U: 0 }
  const ATTACK = 50 // the speed moving to the next mouth shape animation
  const RELEASE = 30 // the speed ending the current mouth shape animation
  const CAP = 0.7
  const SILENCE_VOL = 0.04
  const SILENCE_GAIN = 0.05
  const IDLE_MS = 160
  let lastActiveAt = 0

  // Resolve the actual expression names for a VRM model
  function resolveExpressionNames(vrm: VRMCore): void {
    const vrmId = vrm.scene?.uuid ?? ''
    if (lastVrmId === vrmId)
      return // Already resolved for this model

    lastVrmId = vrmId
    resolvedBlendshapeMap = { A: null, E: null, I: null, O: null, U: null }

    if (!vrm.expressionManager)
      return

    const availableExpressions = Object.keys(vrm.expressionManager.expressionMap)
    console.debug('[lip-sync] Available expressions:', availableExpressions)

    for (const key of LIP_KEYS) {
      // Try default name first
      const defaultName = DEFAULT_BLENDSHAPE_MAP[key]
      if (availableExpressions.includes(defaultName)) {
        resolvedBlendshapeMap[key] = defaultName
        continue
      }

      // Try alternative names
      for (const altName of ALTERNATIVE_NAMES[key]) {
        if (availableExpressions.includes(altName)) {
          resolvedBlendshapeMap[key] = altName
          console.debug(`[lip-sync] Mapped ${key} -> ${altName} (alternative)`)
          break
        }
      }

      // Try case-insensitive match as last resort
      if (!resolvedBlendshapeMap[key]) {
        const lowerDefault = defaultName.toLowerCase()
        const match = availableExpressions.find(e => e.toLowerCase() === lowerDefault)
        if (match) {
          resolvedBlendshapeMap[key] = match
          console.debug(`[lip-sync] Mapped ${key} -> ${match} (case-insensitive)`)
        }
      }
    }

    const resolved = Object.entries(resolvedBlendshapeMap)
      .filter(([_, v]) => v !== null)
      .map(([k, v]) => `${k}:${v}`)
    const missing = LIP_KEYS.filter(k => !resolvedBlendshapeMap[k])

    if (missing.length > 0) {
      console.warn(`[lip-sync] Missing viseme expressions: ${missing.join(', ')}. Available: ${availableExpressions.join(', ')}`)
    }
    else {
      console.debug(`[lip-sync] Resolved expressions: ${resolved.join(', ')}`)
    }
  }

  watch([isReady, audioNode], ([ready, newAudioNode], [, oldAudioNode]) => {
    if (oldAudioNode && oldAudioNode !== newAudioNode) {
      try {
        oldAudioNode.disconnect()
      }
      catch {}
    }
    if (!ready || !newAudioNode || !lipSyncNode.value)
      return
    try {
      newAudioNode.connect(lipSyncNode.value)
    }
    catch {}
  }, { immediate: true })
  onUnmounted(() => audioNode.value?.disconnect())

  function update(vrm?: VRMCore, delta = 0.016) {
    const node = lipSyncNode.value
    if (!vrm?.expressionManager || !node)
      return

    // Resolve expression names for this VRM model (cached per model)
    resolveExpressionNames(vrm)

    const vol = node.volume ?? 0
    const amp = Math.min(vol * 0.9, 1) ** 0.7

    // Remapping wLipSync output AEIOUS to AEIOU
    const projected: Record<LipKey, number> = { A: 0, E: 0, I: 0, O: 0, U: 0 }
    for (const raw of RAW_KEYS) {
      const lip = RAW_TO_LIP[raw]
      const rawVal = node.weights[raw] ?? 0
      projected[lip] = Math.max(projected[lip], rawVal * amp)
    }

    // winner + runner
    // Original code: all AEIOU mouth shape blended together. Because the A mouth shape has the largest deformation, mixing A-E-I-O-U based on their raw weights causes the combined result to be biased heavily toward A in most cases.
    // Improved code: Only the 2 mouth shapes with the largest weights will be blended.
    let winner: LipKey = 'I'
    let runner: LipKey = 'E'
    let winnerVal = -Infinity
    let runnerVal = -Infinity
    for (const key of LIP_KEYS) {
      const val = projected[key]
      if (val > winnerVal) {
        runnerVal = winnerVal
        runner = winner
        winnerVal = val
        winner = key
      }
      else if (val > runnerVal) {
        runnerVal = val
        runner = key
      }
    }

    // Detect pause or keep silence
    const now = performance.now()
    let silent = amp < SILENCE_VOL || winnerVal < SILENCE_GAIN
    if (!silent)
      lastActiveAt = now
    if (now - lastActiveAt > IDLE_MS)
      silent = true

    // winner + runner weights
    const target: Record<LipKey, number> = { A: 0, E: 0, I: 0, O: 0, U: 0 }
    if (!silent) {
      target[winner] = Math.min(CAP, winnerVal)
      target[runner] = Math.min(CAP * 0.5, runnerVal * 0.6)
    }

    // smoothness and expression generation
    for (const key of LIP_KEYS) {
      const expressionName = resolvedBlendshapeMap[key]
      if (!expressionName)
        continue // Skip if no matching expression found for this viseme

      const from = smoothState[key]
      const to = target[key]
      // lerp
      const rate = 1 - Math.exp(-(to > from ? ATTACK : RELEASE) * delta)
      smoothState[key] = from + (to - from) * rate
      const weight = (smoothState[key] <= 0.01 ? 0 : smoothState[key]) * 0.7
      vrm.expressionManager.setValue(expressionName, weight)
    }
  }

  return { update }
}
